import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  ChevronRight, 
  AlertCircle, 
  X, 
  Plus, 
  Trash2, 
  Search,
  Package,
  Settings,
  CheckCircle2,
  ArrowLeft,
  Factory,
  Calendar,
  ClipboardList,
  Info,
  TrendingUp,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { useNavigation } from "@/components/layout/app-layout";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { generateMockStockItems } from "@/data/mockStockItems";
import { MOCK_BOMS } from "@/data/mockBOMs";
import { mockWarehouses } from "@/data/mockWarehousesLocations";
import { getActiveProjects, getProjectByCode, mockProjects } from "@/data/mockProjects";
import { isFeatureEnabled } from "@/config/feature-flags";

interface ValidationErrors {
  [key: string]: string;
}

interface WorkOrderMaterial {
  id: string;
  lineNumber: number;
  stockItemCode: string;
  stockItemName: string;
  description: string;
  quantityRequired: number;
  quantityIssued: number;
  quantityOutstanding: number;
  unitCost: number;
  totalCost: number;
  warehouseCode?: string;
  warehouseName?: string;
}

interface WorkOrder {
  id: string;
  workOrderNumber: string;
  productCode: string;
  productName: string;
  description: string;
  quantityOrdered: number;
  quantityCompleted: number;
  quantityOutstanding: number;
  status: "planned" | "released" | "in-progress" | "completed" | "on-hold" | "cancelled";
  priority: "low" | "normal" | "high" | "urgent";
  startDate: Date;
  dueDate: Date;
  completionDate?: Date;
  bomNumber?: string;
  bomVersion?: string;
  projectCode?: string;
  projectName?: string;
  warehouseCode: string;
  warehouseName: string;
  estimatedCost: number;
  actualCost: number;
  currency: string;
  notes: string;
  materials: WorkOrderMaterial[];
  createdBy: string;
  createdDate: Date;
  lastModifiedBy: string;
  lastModifiedDate: Date;
}

interface CreateWorkOrderPageProps {
  onWorkOrderCreated?: (workOrder: WorkOrder) => void;
}

export function CreateWorkOrderPage({ onWorkOrderCreated }: CreateWorkOrderPageProps) {
  const { navigateTo, pageParams } = useNavigation();
  const editMode = pageParams?.editMode || false;
  const editWorkOrderData = pageParams?.workOrderData as WorkOrder | undefined;
  const [currentStep, setCurrentStep] = useState(1);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [showBOMPicker, setShowBOMPicker] = useState(false);
  const [showMaterialPicker, setShowMaterialPicker] = useState(false);
  const [productSearchQuery, setProductSearchQuery] = useState("");
  const [bomSearchQuery, setBomSearchQuery] = useState("");
  const [materialSearchQuery, setMaterialSearchQuery] = useState("");
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]);
  const [showBulkEditDialog, setShowBulkEditDialog] = useState(false);
  const [bulkEditPercentage, setBulkEditPercentage] = useState<number>(10);
  const [showProjectPicker, setShowProjectPicker] = useState(false);
  const [projectSearchQuery, setProjectSearchQuery] = useState("");

  // Get available stock items, BOMs, and projects
  const stockItems = generateMockStockItems();
  const boms = MOCK_BOMS;
  const hasFinancialsIntegration = isFeatureEnabled("enableFinancialsIntegration");
  const activeProjects = hasFinancialsIntegration ? getActiveProjects() : [];

  // Initialize new work order with defaults or edit data
  const [newWorkOrder, setNewWorkOrder] = useState<Partial<WorkOrder>>(() => {
    if (editMode && editWorkOrderData) {
      return editWorkOrderData;
    }
    return {
      workOrderNumber: `WO-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`,
      productCode: "",
      productName: "",
      description: "",
      quantityOrdered: 1,
      quantityCompleted: 0,
      quantityOutstanding: 1,
      status: "planned",
      priority: "normal",
      startDate: new Date(),
      dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
      bomNumber: "",
      bomVersion: "",
      warehouseCode: "WH-MAIN",
      warehouseName: "Main Warehouse",
      estimatedCost: 0,
      actualCost: 0,
      currency: "GBP",
      notes: "",
      materials: [],
    };
  });

  // Check for draft on mount (skip if in edit mode)
  useEffect(() => {
    if (editMode) return;
    
    const draft = localStorage.getItem("workOrderDraft");
    if (draft) {
      try {
        const parsedDraft = JSON.parse(draft);
        const shouldRestore = window.confirm("You have an unsaved draft. Would you like to restore it?");
        if (shouldRestore) {
          setNewWorkOrder(parsedDraft);
          setHasUnsavedChanges(true);
          toast.info("Draft restored");
        } else {
          localStorage.removeItem("workOrderDraft");
        }
      } catch (error) {
        console.error("Failed to parse draft:", error);
        localStorage.removeItem("workOrderDraft");
      }
    }
  }, [editMode]);

  // Auto-save draft every 30 seconds
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const interval = setInterval(() => {
      localStorage.setItem("workOrderDraft", JSON.stringify(newWorkOrder));
    }, 30000);

    return () => clearInterval(interval);
  }, [newWorkOrder, hasUnsavedChanges]);

  // Calculate costs whenever materials change
  useEffect(() => {
    const estimatedCost = (newWorkOrder.materials || []).reduce((sum, mat) => sum + mat.totalCost, 0);

    setNewWorkOrder(prev => ({
      ...prev,
      estimatedCost,
      quantityOutstanding: (prev.quantityOrdered || 0) - (prev.quantityCompleted || 0),
    }));
  }, [newWorkOrder.materials, newWorkOrder.quantityOrdered, newWorkOrder.quantityCompleted]);

  // Validate field
  const validateField = (field: string, value: any): string => {
    switch (field) {
      case "workOrderNumber":
        if (!value || value.trim() === "") return "Work order number is required";
        return "";
      
      case "productCode":
        if (!value || value.trim() === "") return "Product is required";
        return "";
      
      case "quantityOrdered":
        if (!value || value <= 0) return "Quantity must be greater than 0";
        return "";
      
      case "startDate":
        if (!value) return "Start date is required";
        return "";
      
      case "dueDate":
        if (!value) return "Due date is required";
        if (newWorkOrder.startDate && new Date(value) < new Date(newWorkOrder.startDate)) {
          return "Due date must be after start date";
        }
        return "";
      
      case "warehouseCode":
        // Warehouse is optional
        return "";
      
      default:
        return "";
    }
  };

  // Handle field change
  const handleFieldChange = (field: keyof WorkOrder, value: any) => {
    setNewWorkOrder(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
    
    // Mark field as touched
    setTouchedFields(prev => new Set(prev).add(field));
    
    // Validate field
    const error = validateField(field, value);
    setValidationErrors(prev => ({
      ...prev,
      [field]: error
    }));
  };

  // Handle field blur
  const handleFieldBlur = (field: keyof WorkOrder) => {
    setTouchedFields(prev => new Set(prev).add(field));
    const error = validateField(field, newWorkOrder[field]);
    setValidationErrors(prev => ({
      ...prev,
      [field]: error
    }));
  };

  // Handle product selection
  const handleSelectProduct = (itemCode: string) => {
    const item = stockItems.find(i => i.code === itemCode);
    if (item) {
      setNewWorkOrder(prev => ({
        ...prev,
        productCode: item.code,
        productName: item.description,
        description: item.description,
      }));
      setHasUnsavedChanges(true);
      setShowProductPicker(false);
      setProductSearchQuery("");
      
      // Auto-populate BOM if available
      const matchingBOM = boms.find(bom => bom.parentItemCode === item.code && bom.status === "active");
      if (matchingBOM) {
        handleSelectBOM(matchingBOM.bomNumber);
        toast.success("Product selected and BOM auto-populated");
      }
    }
  };

  // Handle BOM selection
  const handleSelectBOM = (bomNumber: string) => {
    const bom = boms.find(b => b.bomNumber === bomNumber);
    if (bom) {
      // Convert BOM components to work order materials
      const materials: WorkOrderMaterial[] = bom.components.map((comp, index) => ({
        id: `m${Date.now()}-${index}`,
        lineNumber: (index + 1) * 10,
        stockItemCode: comp.componentCode,
        stockItemName: comp.componentName,
        description: comp.componentDescription,
        quantityRequired: comp.quantity * (newWorkOrder.quantityOrdered || 1),
        quantityIssued: 0,
        quantityOutstanding: comp.quantity * (newWorkOrder.quantityOrdered || 1),
        unitCost: comp.unitCost,
        totalCost: comp.totalCost * (newWorkOrder.quantityOrdered || 1),
        warehouseCode: newWorkOrder.warehouseCode,
        warehouseName: newWorkOrder.warehouseName,
      }));

      setNewWorkOrder(prev => ({
        ...prev,
        bomNumber: bom.bomNumber,
        bomVersion: bom.version,
        materials,
      }));
      setHasUnsavedChanges(true);
      setShowBOMPicker(false);
      setBomSearchQuery("");
      toast.success(`BOM ${bom.bomNumber} loaded with ${materials.length} materials`);
    }
  };

  // Add material(s)
  const handleAddMaterials = () => {
    if (selectedMaterials.length === 0) return;

    const newMaterials: WorkOrderMaterial[] = selectedMaterials.map((itemCode, index) => {
      const item = stockItems.find(i => i.code === itemCode);
      if (!item) return null;

      return {
        id: `m${Date.now()}-${index}`,
        lineNumber: ((newWorkOrder.materials?.length || 0) + index + 1) * 10,
        stockItemCode: item.code,
        stockItemName: item.description,
        description: item.description,
        quantityRequired: 1,
        quantityIssued: 0,
        quantityOutstanding: 1,
        unitCost: item.unitPrice || 0,
        totalCost: item.unitPrice || 0,
        warehouseCode: newWorkOrder.warehouseCode,
        warehouseName: newWorkOrder.warehouseName,
      };
    }).filter(Boolean) as WorkOrderMaterial[];

    setNewWorkOrder(prev => ({
      ...prev,
      materials: [...(prev.materials || []), ...newMaterials],
    }));
    setHasUnsavedChanges(true);
    setShowMaterialPicker(false);
    setMaterialSearchQuery("");
    setSelectedMaterials([]);
    
    toast.success(`Added ${newMaterials.length} material${newMaterials.length > 1 ? 's' : ''}`);
  };

  // Toggle material selection
  const toggleMaterialSelection = (itemCode: string) => {
    setSelectedMaterials(prev => 
      prev.includes(itemCode) 
        ? prev.filter(code => code !== itemCode)
        : [...prev, itemCode]
    );
  };

  // Update material
  const handleUpdateMaterial = (id: string, field: keyof WorkOrderMaterial, value: any) => {
    setNewWorkOrder(prev => ({
      ...prev,
      materials: (prev.materials || []).map(mat => {
        if (mat.id === id) {
          const updated = { ...mat, [field]: value };
          // Recalculate total cost and outstanding
          if (field === "quantityRequired" || field === "unitCost") {
            updated.totalCost = updated.quantityRequired * updated.unitCost;
            updated.quantityOutstanding = updated.quantityRequired - updated.quantityIssued;
          }
          if (field === "quantityIssued") {
            updated.quantityOutstanding = updated.quantityRequired - updated.quantityIssued;
          }
          return updated;
        }
        return mat;
      }),
    }));
    setHasUnsavedChanges(true);
  };

  // Remove material
  const handleRemoveMaterial = (id: string) => {
    setNewWorkOrder(prev => ({
      ...prev,
      materials: (prev.materials || []).filter(mat => mat.id !== id),
    }));
    setHasUnsavedChanges(true);
  };

  // Bulk edit - apply percentage increase to all quantities
  const handleBulkEditQuantities = () => {
    const multiplier = 1 + (bulkEditPercentage / 100);
    setNewWorkOrder(prev => ({
      ...prev,
      materials: (prev.materials || []).map(mat => {
        const newQuantity = Math.ceil(mat.quantityRequired * multiplier * 100) / 100; // Round to 2 decimals
        return {
          ...mat,
          quantityRequired: newQuantity,
          quantityOutstanding: newQuantity - mat.quantityIssued,
          totalCost: newQuantity * mat.unitCost,
        };
      }),
    }));
    setHasUnsavedChanges(true);
    setShowBulkEditDialog(false);
    toast.success(`Applied ${bulkEditPercentage}% increase to all material quantities`);
  };

  // Update all costs from current stock prices
  const handleUpdateAllCosts = () => {
    setNewWorkOrder(prev => ({
      ...prev,
      materials: (prev.materials || []).map(mat => {
        const stockItem = stockItems.find(item => item.code === mat.stockItemCode);
        const newCost = stockItem?.unitPrice || mat.unitCost;
        return {
          ...mat,
          unitCost: newCost,
          totalCost: mat.quantityRequired * newCost,
        };
      }),
    }));
    setHasUnsavedChanges(true);
    toast.success("Updated all material costs from current stock prices");
  };

  // Validate step
  const validateStep = (step: number): boolean => {
    const errors: ValidationErrors = {};

    if (step === 1) {
      // Basic Details
      ["workOrderNumber", "productCode", "quantityOrdered", "startDate", "dueDate", "warehouseCode"].forEach(field => {
        const error = validateField(field, newWorkOrder[field as keyof WorkOrder]);
        if (error) errors[field] = error;
      });
    } else if (step === 2) {
      // Materials - at least one required
      if (!newWorkOrder.materials || newWorkOrder.materials.length === 0) {
        errors.materials = "At least one material is required";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle next step
  const handleNextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    } else {
      toast.error("Please fix the errors before continuing");
    }
  };

  // Handle previous step
  const handlePreviousStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Handle save
  const handleSave = async () => {
    // Validate all steps
    if (!validateStep(1) || !validateStep(2)) {
      toast.error("Please fix all errors before saving");
      setCurrentStep(1);
      return;
    }

    setIsSaving(true);

    try {
      const finalWorkOrder: WorkOrder = {
        id: editMode && editWorkOrderData ? editWorkOrderData.id : String(Date.now()),
        workOrderNumber: newWorkOrder.workOrderNumber!,
        productCode: newWorkOrder.productCode!,
        productName: newWorkOrder.productName!,
        description: newWorkOrder.description!,
        quantityOrdered: newWorkOrder.quantityOrdered!,
        quantityCompleted: newWorkOrder.quantityCompleted || 0,
        quantityOutstanding: newWorkOrder.quantityOutstanding!,
        status: newWorkOrder.status!,
        priority: newWorkOrder.priority!,
        startDate: newWorkOrder.startDate!,
        dueDate: newWorkOrder.dueDate!,
        completionDate: newWorkOrder.completionDate,
        bomNumber: newWorkOrder.bomNumber,
        bomVersion: newWorkOrder.bomVersion,
        projectCode: newWorkOrder.projectCode,
        projectName: newWorkOrder.projectName,
        warehouseCode: newWorkOrder.warehouseCode!,
        warehouseName: newWorkOrder.warehouseName!,
        estimatedCost: newWorkOrder.estimatedCost!,
        actualCost: newWorkOrder.actualCost || 0,
        currency: newWorkOrder.currency!,
        notes: newWorkOrder.notes || "",
        materials: newWorkOrder.materials!,
        createdBy: editMode && editWorkOrderData ? editWorkOrderData.createdBy : "Current User",
        createdDate: editMode && editWorkOrderData ? editWorkOrderData.createdDate : new Date(),
        lastModifiedBy: "Current User",
        lastModifiedDate: new Date(),
      };

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));

      // Save to localStorage
      const savedWorkOrders = localStorage.getItem("workOrders");
      let workOrdersList: WorkOrder[] = [];
      
      if (savedWorkOrders) {
        try {
          workOrdersList = JSON.parse(savedWorkOrders);
        } catch (e) {
          console.error("Failed to parse saved work orders:", e);
        }
      }

      if (editMode && editWorkOrderData) {
        // Update existing work order
        const index = workOrdersList.findIndex(wo => wo.id === editWorkOrderData.id);
        if (index !== -1) {
          workOrdersList[index] = finalWorkOrder;
        }
      } else {
        // Add new work order to the beginning of the list
        workOrdersList.unshift(finalWorkOrder);
      }

      localStorage.setItem("workOrders", JSON.stringify(workOrdersList));

      toast.success(editMode ? `Work Order ${finalWorkOrder.workOrderNumber} updated successfully` : `Work Order ${finalWorkOrder.workOrderNumber} created successfully`);

      // Clear draft
      localStorage.removeItem("workOrderDraft");

      // Call callback if provided
      onWorkOrderCreated?.(finalWorkOrder);

      // Navigate back to work orders page
      navigateTo("work-orders");
    } catch (error) {
      toast.error(editMode ? "Failed to update work order" : "Failed to create work order");
      console.error("Error saving work order:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowCancelDialog(true);
    } else {
      navigateTo("work-orders");
    }
  };

  // Filter stock items for product picker (only show items that can be assembled)
  const filteredProducts = stockItems.filter(item =>
    item.isAssembly &&
    (item.code.toLowerCase().includes(productSearchQuery.toLowerCase()) ||
    item.description.toLowerCase().includes(productSearchQuery.toLowerCase()))
  );

  // Filter BOMs for BOM picker
  const filteredBOMs = boms.filter(bom =>
    bom.status === "active" &&
    (!newWorkOrder.productCode || bom.parentItemCode === newWorkOrder.productCode) &&
    (bom.bomNumber.toLowerCase().includes(bomSearchQuery.toLowerCase()) ||
    bom.parentItemName.toLowerCase().includes(bomSearchQuery.toLowerCase()))
  );

  // Filter stock items for material picker
  const filteredMaterials = stockItems.filter(item =>
    item.isComponent &&
    (item.code.toLowerCase().includes(materialSearchQuery.toLowerCase()) ||
    item.description.toLowerCase().includes(materialSearchQuery.toLowerCase()))
  );

  // Get error count
  const errorCount = Object.values(validationErrors).filter(e => e !== "").length;

  // Step indicator
  const steps = [
    { number: 1, title: "Basic Details", icon: Settings },
    { number: 2, title: "Materials", icon: Package },
    { number: 3, title: "Review", icon: CheckCircle2 },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={handleCancel}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold tracking-tight">
              {editMode ? `Edit Work Order - ${newWorkOrder.workOrderNumber}` : "Create New Work Order"}
            </h1>
            <p className="text-muted-foreground mt-2">
              {editMode ? "Update the work order details" : "Create a new manufacturing or assembly work order"}
            </p>
          </div>
          <Badge 
            variant="outline" 
            className={
              newWorkOrder.status === "planned" ? "bg-blue-50 text-blue-700 border-blue-200" :
              newWorkOrder.status === "released" ? "bg-purple-50 text-purple-700 border-purple-200" :
              newWorkOrder.status === "in-progress" ? "bg-yellow-50 text-yellow-700 border-yellow-200" :
              newWorkOrder.status === "completed" ? "bg-green-50 text-green-700 border-green-200" :
              newWorkOrder.status === "on-hold" ? "bg-orange-50 text-orange-700 border-orange-200" :
              "bg-gray-50 text-gray-700 border-gray-200"
            }
          >
            {newWorkOrder.status}
          </Badge>
        </div>
      </div>

      {/* Step Indicator */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={step.number} className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`flex items-center justify-center w-12 h-12 rounded-full border-2 transition-colors ${
                    currentStep === step.number
                      ? "border-primary bg-primary text-primary-foreground"
                      : currentStep > step.number
                      ? "border-green-500 bg-green-500 text-white"
                      : "border-muted-foreground/25 bg-background text-muted-foreground"
                  }`}
                >
                  {currentStep > step.number ? (
                    <CheckCircle2 className="h-6 w-6" />
                  ) : (
                    <step.icon className="h-6 w-6" />
                  )}
                </div>
                <p
                  className={`mt-2 text-sm font-medium ${
                    currentStep === step.number
                      ? "text-primary"
                      : currentStep > step.number
                      ? "text-green-600"
                      : "text-muted-foreground"
                  }`}
                >
                  {step.title}
                </p>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`h-0.5 flex-1 mx-4 transition-colors ${
                    currentStep > step.number ? "bg-green-500" : "bg-muted-foreground/25"
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Error Summary */}
      {errorCount > 0 && touchedFields.size > 0 && (
        <Card className="p-4 border-destructive bg-destructive/5">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
            <div>
              <p className="font-medium text-destructive">
                Please fix {errorCount} error{errorCount > 1 ? 's' : ''} before continuing
              </p>
              <ul className="mt-2 space-y-1 text-sm text-destructive/80">
                {Object.entries(validationErrors).map(([field, error]) => 
                  error && (
                    <li key={field}>• {error}</li>
                  )
                )}
              </ul>
            </div>
          </div>
        </Card>
      )}

      {/* Form Content */}
      <Card className="p-6">
        {/* Step 1: Basic Details */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold">Basic Details</h2>

            {/* Work Order Number */}
            <div className="space-y-2">
              <Label htmlFor="workOrderNumber" className="flex items-center gap-1">
                Work Order Number <span className="text-destructive">*</span>
              </Label>
              <Input
                id="workOrderNumber"
                value={newWorkOrder.workOrderNumber}
                onChange={(e) => handleFieldChange("workOrderNumber", e.target.value)}
                onBlur={() => handleFieldBlur("workOrderNumber")}
                placeholder="WO-2024-001"
                className={validationErrors.workOrderNumber && touchedFields.has("workOrderNumber") ? "border-destructive" : ""}
              />
              {validationErrors.workOrderNumber && touchedFields.has("workOrderNumber") && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <X className="h-3 w-3" />
                  {validationErrors.workOrderNumber}
                </p>
              )}
            </div>

            {/* Product */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                Product <span className="text-destructive">*</span>
              </Label>
              <div className="flex gap-2">
                <Input
                  value={newWorkOrder.productCode ? `${newWorkOrder.productCode} - ${newWorkOrder.productName}` : ""}
                  placeholder="Select product to manufacture"
                  readOnly
                  className={`flex-1 ${validationErrors.productCode && touchedFields.has("productCode") ? "border-destructive" : ""}`}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowProductPicker(true)}
                >
                  <Search className="h-4 w-4 mr-2" />
                  Browse
                </Button>
              </div>
              {validationErrors.productCode && touchedFields.has("productCode") && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <X className="h-3 w-3" />
                  {validationErrors.productCode}
                </p>
              )}
            </div>

            {/* BOM Selection (Optional) */}
            <div className="space-y-2">
              <Label>Bill of Materials (Optional)</Label>
              <div className="flex gap-2">
                <Input
                  value={newWorkOrder.bomNumber ? `${newWorkOrder.bomNumber} (v${newWorkOrder.bomVersion})` : ""}
                  placeholder="Select BOM to auto-populate materials"
                  readOnly
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowBOMPicker(true)}
                  disabled={!newWorkOrder.productCode}
                >
                  <Search className="h-4 w-4 mr-2" />
                  Browse
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Selecting a BOM will automatically populate the materials list
              </p>
            </div>

            {/* Quantity and Priority */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="quantityOrdered" className="flex items-center gap-1">
                  Quantity to Produce <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="quantityOrdered"
                  type="number"
                  value={newWorkOrder.quantityOrdered}
                  onChange={(e) => handleFieldChange("quantityOrdered", parseFloat(e.target.value) || 0)}
                  onBlur={() => handleFieldBlur("quantityOrdered")}
                  min="0"
                  step="1"
                  className={validationErrors.quantityOrdered && touchedFields.has("quantityOrdered") ? "border-destructive" : ""}
                />
                {validationErrors.quantityOrdered && touchedFields.has("quantityOrdered") && (
                  <p className="text-sm text-destructive flex items-center gap-1">
                    <X className="h-3 w-3" />
                    {validationErrors.quantityOrdered}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select
                  value={newWorkOrder.priority}
                  onValueChange={(value: "low" | "normal" | "high" | "urgent") => handleFieldChange("priority", value)}
                >
                  <SelectTrigger id="priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={newWorkOrder.status}
                onValueChange={(value: "planned" | "released" | "in-progress" | "completed" | "on-hold" | "cancelled") => handleFieldChange("status", value)}
              >
                <SelectTrigger id="status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="planned">Planned</SelectItem>
                  <SelectItem value="released">Released</SelectItem>
                  <SelectItem value="in-progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="on-hold">On Hold</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate" className="flex items-center gap-1">
                  Start Date <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="startDate"
                  type="date"
                  value={newWorkOrder.startDate ? new Date(newWorkOrder.startDate).toISOString().split('T')[0] : ""}
                  onChange={(e) => handleFieldChange("startDate", new Date(e.target.value))}
                  onBlur={() => handleFieldBlur("startDate")}
                  className={validationErrors.startDate && touchedFields.has("startDate") ? "border-destructive" : ""}
                />
                {validationErrors.startDate && touchedFields.has("startDate") && (
                  <p className="text-sm text-destructive flex items-center gap-1">
                    <X className="h-3 w-3" />
                    {validationErrors.startDate}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="dueDate" className="flex items-center gap-1">
                  Due Date <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="dueDate"
                  type="date"
                  value={newWorkOrder.dueDate ? new Date(newWorkOrder.dueDate).toISOString().split('T')[0] : ""}
                  onChange={(e) => handleFieldChange("dueDate", new Date(e.target.value))}
                  onBlur={() => handleFieldBlur("dueDate")}
                  className={validationErrors.dueDate && touchedFields.has("dueDate") ? "border-destructive" : ""}
                />
                {validationErrors.dueDate && touchedFields.has("dueDate") && (
                  <p className="text-sm text-destructive flex items-center gap-1">
                    <X className="h-3 w-3" />
                    {validationErrors.dueDate}
                  </p>
                )}
              </div>
            </div>

            {/* Warehouse */}
            <div className="space-y-2">
              <Label htmlFor="warehouseCode">
                Warehouse (Optional)
              </Label>
              <Select
                value={newWorkOrder.warehouseCode}
                onValueChange={(value) => {
                  const warehouse = mockWarehouses.find(w => w.code === value);
                  handleFieldChange("warehouseCode", value);
                  handleFieldChange("warehouseName", warehouse?.name || "");
                }}
              >
                <SelectTrigger id="warehouseCode" className={validationErrors.warehouseCode && touchedFields.has("warehouseCode") ? "border-destructive" : ""}>
                  <SelectValue placeholder="Select warehouse (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {mockWarehouses.filter(w => w.active).map((warehouse) => (
                    <SelectItem key={warehouse.code} value={warehouse.code}>
                      {warehouse.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {validationErrors.warehouseCode && touchedFields.has("warehouseCode") && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <X className="h-3 w-3" />
                  {validationErrors.warehouseCode}
                </p>
              )}
            </div>

            {/* Project (Optional) */}
            {hasFinancialsIntegration ? (
              // With Financials Integration - Show dropdown
              <div className="space-y-2">
                <Label htmlFor="projectCode">Project (Optional)</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                    onClick={() => setShowProjectPicker(true)}
                  >
                    {newWorkOrder.projectCode ? (
                      <span className="flex items-center gap-2">
                        <ClipboardList className="h-4 w-4" />
                        {newWorkOrder.projectCode} - {newWorkOrder.projectName}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Select project (optional)</span>
                    )}
                  </Button>
                  {newWorkOrder.projectCode && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        handleFieldChange("projectCode", "");
                        handleFieldChange("projectName", "");
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              // Without Integration - Show text inputs
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="projectCode">Project Code (Optional)</Label>
                  <Input
                    id="projectCode"
                    value={newWorkOrder.projectCode || ""}
                    onChange={(e) => handleFieldChange("projectCode", e.target.value)}
                    placeholder="PRJ-001"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="projectName">Project Name (Optional)</Label>
                  <Input
                    id="projectName"
                    value={newWorkOrder.projectName || ""}
                    onChange={(e) => handleFieldChange("projectName", e.target.value)}
                    placeholder="Q1 Production"
                  />
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={newWorkOrder.notes}
                onChange={(e) => handleFieldChange("notes", e.target.value)}
                placeholder="Add any additional notes about this work order"
                rows={3}
              />
            </div>
          </div>
        )}

        {/* Step 2: Materials */}
        {currentStep === 2 && (
          <div className="space-y-6">
            {/* Product Context */}
            {newWorkOrder.productCode && (
              <Card className="p-4 bg-muted/50">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Factory className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Manufacturing</p>
                    <p className="font-semibold text-lg">
                      {newWorkOrder.productCode} - {newWorkOrder.productName}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Quantity: {newWorkOrder.quantityOrdered} units • Priority: {newWorkOrder.priority}
                      {newWorkOrder.bomNumber && ` • BOM: ${newWorkOrder.bomNumber}`}
                    </p>
                  </div>
                </div>
              </Card>
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold">Materials Required</h2>
                {newWorkOrder.bomNumber ? (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <ClipboardList className="h-3 w-3" />
                    From BOM {newWorkOrder.bomNumber}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="flex items-center gap-1">
                    <Package className="h-3 w-3" />
                    Manual Entry
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {newWorkOrder.materials && newWorkOrder.materials.length > 0 && (
                  <>
                    <Button variant="outline" size="sm" onClick={handleUpdateAllCosts}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Update Costs
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setShowBulkEditDialog(true)}>
                      <TrendingUp className="h-4 w-4 mr-2" />
                      Bulk Edit
                    </Button>
                  </>
                )}
                <Button onClick={() => setShowMaterialPicker(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Material
                </Button>
              </div>
            </div>

            {/* Helper message when no BOM selected */}
            {!newWorkOrder.bomNumber && (!newWorkOrder.materials || newWorkOrder.materials.length === 0) && (
              <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg border border-muted">
                <Info className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <p className="text-sm text-muted-foreground">
                  No BOM selected. You can manually add materials or go back to Step 1 to select a BOM for auto-population.
                </p>
              </div>
            )}

            {validationErrors.materials && (
              <Card className="p-4 border-destructive bg-destructive/5">
                <p className="text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {validationErrors.materials}
                </p>
              </Card>
            )}

            {newWorkOrder.materials && newWorkOrder.materials.length > 0 ? (
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">Line</TableHead>
                      <TableHead>Material Code</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="w-28">Qty Required</TableHead>
                      <TableHead className="w-24">Available</TableHead>
                      <TableHead className="w-28">Qty Issued</TableHead>
                      <TableHead className="w-28">Outstanding</TableHead>
                      <TableHead className="w-32">Unit Cost</TableHead>
                      <TableHead className="w-32">Total Cost</TableHead>
                      <TableHead className="w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {newWorkOrder.materials.map((material) => {
                      const stockItem = stockItems.find(item => item.code === material.stockItemCode);
                      const availableQty = stockItem?.quantityOnHand || 0;
                      const isLowStock = availableQty < material.quantityRequired;
                      
                      return (
                        <TableRow key={material.id}>
                          <TableCell>{material.lineNumber}</TableCell>
                          <TableCell className="font-medium">{material.stockItemCode}</TableCell>
                          <TableCell>{material.stockItemName}</TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={material.quantityRequired}
                              onChange={(e) => handleUpdateMaterial(material.id, "quantityRequired", parseFloat(e.target.value) || 0)}
                              min="0"
                              step="0.01"
                              className="w-24"
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <span className={isLowStock ? "text-destructive font-medium" : "text-muted-foreground"}>
                                {availableQty}
                              </span>
                              {isLowStock && (
                                <AlertCircle className="h-3 w-3 text-destructive" />
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={material.quantityIssued}
                              onChange={(e) => handleUpdateMaterial(material.id, "quantityIssued", parseFloat(e.target.value) || 0)}
                              min="0"
                              step="0.01"
                              className="w-24"
                            />
                          </TableCell>
                          <TableCell className="font-medium">
                            {material.quantityOutstanding}
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={material.unitCost}
                              onChange={(e) => handleUpdateMaterial(material.id, "unitCost", parseFloat(e.target.value) || 0)}
                              min="0"
                              step="0.01"
                              className="w-28"
                            />
                          </TableCell>
                          <TableCell className="font-medium">
                            £{material.totalCost.toFixed(2)}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveMaterial(material.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <Card className="p-12 text-center border-dashed">
                <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">No materials added yet</p>
                <Button onClick={() => setShowMaterialPicker(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Material
                </Button>
              </Card>
            )}
          </div>
        )}

        {/* Step 3: Review */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold">Review & Confirm</h2>

            {/* Basic Details Summary */}
            <Card className="p-4">
              <h3 className="font-medium mb-4">Work Order Details</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Work Order Number</p>
                  <p className="font-medium">{newWorkOrder.workOrderNumber}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Status</p>
                  <Badge variant="outline" className="capitalize">{newWorkOrder.status}</Badge>
                </div>
                <div>
                  <p className="text-muted-foreground">Product</p>
                  <p className="font-medium">{newWorkOrder.productCode} - {newWorkOrder.productName}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Quantity</p>
                  <p className="font-medium">{newWorkOrder.quantityOrdered} units</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Priority</p>
                  <Badge 
                    variant="outline"
                    className={
                      newWorkOrder.priority === "urgent" ? "bg-red-50 text-red-700 border-red-200" :
                      newWorkOrder.priority === "high" ? "bg-orange-50 text-orange-700 border-orange-200" :
                      newWorkOrder.priority === "normal" ? "bg-blue-50 text-blue-700 border-blue-200" :
                      "bg-gray-50 text-gray-700 border-gray-200"
                    }
                  >
                    {newWorkOrder.priority}
                  </Badge>
                </div>
                <div>
                  <p className="text-muted-foreground">Warehouse</p>
                  <p className="font-medium">{newWorkOrder.warehouseName}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Start Date</p>
                  <p className="font-medium">{newWorkOrder.startDate ? new Date(newWorkOrder.startDate).toLocaleDateString() : "-"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Due Date</p>
                  <p className="font-medium">{newWorkOrder.dueDate ? new Date(newWorkOrder.dueDate).toLocaleDateString() : "-"}</p>
                </div>
                {newWorkOrder.bomNumber && (
                  <div>
                    <p className="text-muted-foreground">BOM</p>
                    <p className="font-medium">{newWorkOrder.bomNumber} (v{newWorkOrder.bomVersion})</p>
                  </div>
                )}
                {newWorkOrder.projectCode && (
                  <div>
                    <p className="text-muted-foreground">Project</p>
                    <p className="font-medium">{newWorkOrder.projectCode} - {newWorkOrder.projectName}</p>
                  </div>
                )}
              </div>
            </Card>

            {/* Materials Summary */}
            <Card className="p-4">
              <h3 className="font-medium mb-4">Materials ({newWorkOrder.materials?.length || 0})</h3>
              {newWorkOrder.materials && newWorkOrder.materials.length > 0 ? (
                <div className="space-y-2">
                  {newWorkOrder.materials.map((mat) => (
                    <div key={mat.id} className="flex items-center justify-between text-sm border-b pb-2 last:border-0">
                      <div>
                        <p className="font-medium">{mat.stockItemCode}</p>
                        <p className="text-muted-foreground text-xs">{mat.stockItemName}</p>
                      </div>
                      <div className="text-right">
                        <p>Required: {mat.quantityRequired}</p>
                        <p className="text-muted-foreground text-xs">£{mat.totalCost.toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No materials</p>
              )}
            </Card>

            {/* Cost Summary */}
            <Card className="p-4 bg-primary/5">
              <h3 className="font-medium mb-4">Cost Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Estimated Material Cost</span>
                  <span className="font-medium">£{newWorkOrder.estimatedCost?.toFixed(2) || "0.00"}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Cost per Unit (Qty: {newWorkOrder.quantityOrdered})</span>
                  <span>£{((newWorkOrder.estimatedCost || 0) / (newWorkOrder.quantityOrdered || 1)).toFixed(2)}</span>
                </div>
              </div>
            </Card>

            {/* Notes */}
            {newWorkOrder.notes && (
              <Card className="p-4">
                <h3 className="font-medium mb-2">Notes</h3>
                <p className="text-sm text-muted-foreground">{newWorkOrder.notes}</p>
              </Card>
            )}
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t">
          <Button
            variant="outline"
            onClick={handlePreviousStep}
            disabled={currentStep === 1}
          >
            Previous
          </Button>

          <div className="flex gap-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            {currentStep < 3 ? (
              <Button onClick={handleNextStep}>
                Next
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? (editMode ? "Updating..." : "Creating...") : (editMode ? "Update Work Order" : "Create Work Order")}
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Product Picker Dialog */}
      <Dialog open={showProductPicker} onOpenChange={setShowProductPicker}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Select Product</DialogTitle>
            <DialogDescription>
              Choose the product to manufacture
            </DialogDescription>
          </DialogHeader>
          <Command className="rounded-lg border">
            <CommandInput
              placeholder="Search products..."
              value={productSearchQuery}
              onValueChange={setProductSearchQuery}
            />
            <CommandList>
              <CommandEmpty>No products found.</CommandEmpty>
              <CommandGroup>
                {filteredProducts.slice(0, 20).map((item) => (
                  <CommandItem
                    key={item.code}
                    value={item.code}
                    onSelect={() => handleSelectProduct(item.code)}
                  >
                    <div className="flex items-center justify-between w-full">
                      <div>
                        <p className="font-medium">{item.code}</p>
                        <p className="text-sm text-muted-foreground">{item.description}</p>
                      </div>
                      <Badge variant="outline">{item.category}</Badge>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>

      {/* BOM Picker Dialog */}
      <Dialog open={showBOMPicker} onOpenChange={setShowBOMPicker}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Select Bill of Materials</DialogTitle>
            <DialogDescription>
              Choose a BOM to auto-populate materials
            </DialogDescription>
          </DialogHeader>
          <Command className="rounded-lg border">
            <CommandInput
              placeholder="Search BOMs..."
              value={bomSearchQuery}
              onValueChange={setBomSearchQuery}
            />
            <CommandList>
              <CommandEmpty>No BOMs found.</CommandEmpty>
              <CommandGroup>
                {filteredBOMs.slice(0, 20).map((bom) => (
                  <CommandItem
                    key={bom.bomNumber}
                    value={bom.bomNumber}
                    onSelect={() => handleSelectBOM(bom.bomNumber)}
                  >
                    <div className="flex items-center justify-between w-full">
                      <div>
                        <p className="font-medium">{bom.bomNumber}</p>
                        <p className="text-sm text-muted-foreground">
                          {bom.parentItemCode} - {bom.parentItemName} (v{bom.version})
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {bom.components.length} components
                        </p>
                      </div>
                      <Badge variant="outline">{bom.status}</Badge>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>

      {/* Material Picker Dialog */}
      <Dialog open={showMaterialPicker} onOpenChange={setShowMaterialPicker}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Materials</DialogTitle>
            <DialogDescription>
              Select materials to add to this work order
            </DialogDescription>
          </DialogHeader>
          <Command className="rounded-lg border">
            <CommandInput
              placeholder="Search materials..."
              value={materialSearchQuery}
              onValueChange={setMaterialSearchQuery}
            />
            <CommandList>
              <CommandEmpty>No materials found.</CommandEmpty>
              <CommandGroup>
                {filteredMaterials.slice(0, 20).map((item) => (
                  <CommandItem
                    key={item.code}
                    value={item.code}
                    onSelect={() => toggleMaterialSelection(item.code)}
                  >
                    <div className="flex items-center gap-3 w-full">
                      <input
                        type="checkbox"
                        checked={selectedMaterials.includes(item.code)}
                        onChange={() => toggleMaterialSelection(item.code)}
                        className="h-4 w-4"
                      />
                      <div className="flex-1">
                        <p className="font-medium">{item.code}</p>
                        <p className="text-sm text-muted-foreground">{item.description}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">£{item.unitPrice?.toFixed(2) || "0.00"}</p>
                        <p className="text-xs text-muted-foreground">Stock: {item.physicalQuantity}</p>
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowMaterialPicker(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddMaterials} disabled={selectedMaterials.length === 0}>
              Add {selectedMaterials.length > 0 && `(${selectedMaterials.length})`} Material{selectedMaterials.length !== 1 ? 's' : ''}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Edit Dialog */}
      <Dialog open={showBulkEditDialog} onOpenChange={setShowBulkEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk Edit Material Quantities</DialogTitle>
            <DialogDescription>
              Apply a percentage increase to all material quantities. This is useful for adding waste allowance or buffer stock.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="bulkEditPercentage">Percentage Increase</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="bulkEditPercentage"
                  type="number"
                  value={bulkEditPercentage}
                  onChange={(e) => setBulkEditPercentage(parseFloat(e.target.value) || 0)}
                  min="0"
                  max="100"
                  step="1"
                  className="flex-1"
                />
                <span className="text-sm text-muted-foreground">%</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Example: 10% increase on 100 units = 110 units
              </p>
            </div>
            <div className="rounded-lg border p-3 bg-muted/50">
              <p className="text-sm font-medium mb-2">Preview:</p>
              <div className="space-y-1 text-sm">
                {newWorkOrder.materials?.slice(0, 3).map((mat) => {
                  const newQty = Math.ceil(mat.quantityRequired * (1 + bulkEditPercentage / 100) * 100) / 100;
                  return (
                    <div key={mat.id} className="flex justify-between">
                      <span className="text-muted-foreground">{mat.stockItemCode}:</span>
                      <span>
                        {mat.quantityRequired} → <span className="font-medium text-primary">{newQty}</span>
                      </span>
                    </div>
                  );
                })}
                {(newWorkOrder.materials?.length || 0) > 3 && (
                  <p className="text-xs text-muted-foreground pt-1">
                    ...and {(newWorkOrder.materials?.length || 0) - 3} more
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowBulkEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleBulkEditQuantities}>
              Apply Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Project Picker Dialog */}
      <Dialog open={showProjectPicker} onOpenChange={setShowProjectPicker}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Select Project</DialogTitle>
            <DialogDescription>
              Choose a project from your Financials/Dimensions system
            </DialogDescription>
          </DialogHeader>
          <Command className="rounded-lg border">
            <CommandInput
              placeholder="Search projects by code or name..."
              value={projectSearchQuery}
              onValueChange={setProjectSearchQuery}
            />
            <CommandList className="max-h-[400px]">
              <CommandEmpty>No projects found.</CommandEmpty>
              <CommandGroup>
                {activeProjects
                  .filter(
                    (project) =>
                      project.code.toLowerCase().includes(projectSearchQuery.toLowerCase()) ||
                      project.name.toLowerCase().includes(projectSearchQuery.toLowerCase())
                  )
                  .map((project) => (
                    <CommandItem
                      key={project.code}
                      value={project.code}
                      onSelect={() => {
                        handleFieldChange("projectCode", project.code);
                        handleFieldChange("projectName", project.name);
                        setShowProjectPicker(false);
                        setProjectSearchQuery("");
                        toast.success(`Selected project: ${project.code}`);
                      }}
                    >
                      <div className="flex flex-col gap-1 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{project.code}</span>
                          <Badge variant="secondary" className="text-xs">
                            {project.status}
                          </Badge>
                        </div>
                        <span className="text-sm">{project.name}</span>
                        {project.description && (
                          <span className="text-xs text-muted-foreground">{project.description}</span>
                        )}
                        <div className="flex gap-4 text-xs text-muted-foreground mt-1">
                          {project.manager && <span>Manager: {project.manager}</span>}
                          {project.department && <span>Dept: {project.department}</span>}
                        </div>
                      </div>
                    </CommandItem>
                  ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                localStorage.removeItem("workOrderDraft");
                navigateTo("work-orders");
              }}
            >
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
